'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherBookOpen, FeatherChevronDown, FeatherChevronsLeft, FeatherChevronsRight, FeatherDollarSign, FeatherEye, FeatherGavel, FeatherHeartHandshake, FeatherHome, FeatherKey, FeatherLayers, FeatherLayoutDashboard, FeatherListChecks, FeatherMessageCircle, FeatherPiggyBank, FeatherPlus, FeatherPuzzle, FeatherScale, FeatherSend, FeatherSettings, FeatherShare2 } from '@subframe/core';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import React from 'react';
import { selectAuthState } from '@/features/auth/store/selectors';
import { CATEGORIAS_COM_CONTRATO, type CategoriaComContrato, CotaEsgotadaModal, fetchActiveContractByCategory, ROTA_ABA_PLANO, selectActiveContractByCategory, selectActiveContractError, selectActiveContractLoading, selectActiveContractResolvido, selectContractStatus, selectCurrentPeriod, selectEstadoCriacao, selectHasActiveContract, selectIsAtTrialLimit, selectIsCreationBlockedByUsage, selectIsTrialBlocked, selectPodeCriarLoteArbitral } from '@/features/contracts';
import { podeAdministrarChaves } from '@/features/chavesDeApi';
import { IrParaPainelDoRequerido } from '@/features/contextoAcesso';
import { buildSaldoCicloView, pickSidebarSaldos } from '@/features/contracts/saldoCiclo';
import CreateEscritorioModal from '@/features/escritorio/components/CreateEscritorioModal';
import { ChecklistConfiguracao } from '@/features/layout/components/ChecklistConfiguracao';
import { destinoDaCriacao, destinoDaCriacaoArbitral } from '@/features/layout/utils/destinoDaCriacao';
import { useEscritorioSwitcher } from '@/features/escritorio/hooks/useEscritorioSwitcher';
import { buscarAcessoSimulacao, selectPodeUsarSimulacao } from '@/features/negociacaoSimulacao';
import { Badge, SaldoCicloCard, SidebarWithCollapsibleSections, WorkspaceSwitcher } from '@/shared/components/ui';
import { ehAdminDoEscritorio } from '@/shared/utils';
import { registrarAcao } from '@/shared/utils/reportAction';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import styles from './Sidebar.module.css';
interface SidebarProps {
  isMobile?: boolean;
  onNavigate?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}
const getNavItemClassName = (isCollapsed: boolean, extraClassName: string = ''): string => {
  const base = isCollapsed ? 'w-full flex-none' : 'h-9 w-full flex-none';
  return extraClassName ? `${extraClassName} ${base}` : base;
};
const getInteractiveProps = (onActivate: () => void): Pick<React.HTMLAttributes<HTMLDivElement>, 'role' | 'tabIndex' | 'onClick' | 'onKeyDown'> => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  };
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: handleKeyDown
  };
};
interface DisabledNavItemProps {
  label: string;
  icon: React.ReactNode;
  className?: string;
  isCollapsed?: boolean;
}
const DisabledNavItem = t0 => {
  const $ = _c(5);
  const {
    label,
    icon,
    className: t1,
    isCollapsed: t2
  } = t0;
  const className = t1 === undefined ? "h-9" : t1;
  const isCollapsed = t2 === undefined ? false : t2;
  const t3 = `${className} cursor-not-allowed hover:!bg-transparent active:!bg-transparent [&_svg]:!text-neutral-400 [&>span]:!font-normal [&>span]:!text-neutral-400`;
  let t4;
  if ($[0] !== icon || $[1] !== isCollapsed || $[2] !== label || $[3] !== t3) {
    t4 = <SidebarWithCollapsibleSections.NavItem className={t3} icon={icon} isCollapsed={isCollapsed} selected={false} aria-disabled="true">{label}</SidebarWithCollapsibleSections.NavItem>;
    $[0] = icon;
    $[1] = isCollapsed;
    $[2] = label;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const NovoBadge = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Badge variant="brand" className="h-auto px-1.5 py-0 text-[10px]">Novo</Badge>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const Sidebar: React.FC<SidebarProps> = t0 => {
  const $ = _c(102);
  const {
    isMobile: t1,
    onNavigate,
    isCollapsed: t2,
    onToggleCollapse
  } = t0;
  const isMobile = t1 === undefined ? false : t1;
  const isCollapsed = t2 === undefined ? false : t2;
  const {
    selected,
    items,
    loading,
    total,
    query,
    setQuery,
    favorites,
    toggleFavorite,
    loadMore,
    createOpen,
    openCreateModal,
    closeCreateModal,
    handleCreateSuccess,
    selectEscritorio
  } = useEscritorioSwitcher();
  const {
    user
  } = useAppSelector(selectAuthState);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const isDesktopCollapsed = !isMobile && isCollapsed;
  const isGuestWorkspace = !!selected?.guestAccess;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = selectHasActiveContract("NOTIFICACAO");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const hasNotificacaoContract = useAppSelector(t3);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = selectIsAtTrialLimit("NOTIFICACAO");
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const atNotificacaoTrialLimit = useAppSelector(t4);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = selectIsCreationBlockedByUsage("NOTIFICACAO");
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const notificacaoBloqueadaPorConsumo = useAppSelector(t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = selectIsTrialBlocked("NOTIFICACAO");
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const notificacaoBlocked = useAppSelector(t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = selectCurrentPeriod("NOTIFICACAO");
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const notificacaoPeriod = useAppSelector(t7);
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = selectContractStatus("NOTIFICACAO");
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const notificacaoStatus = useAppSelector(t8);
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = selectHasActiveContract("NEGOCIACAO");
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const hasNegociacaoContract = useAppSelector(t9);
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = selectIsAtTrialLimit("NEGOCIACAO");
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const atNegociacaoTrialLimit = useAppSelector(t10);
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = selectIsCreationBlockedByUsage("NEGOCIACAO");
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const negociacaoBloqueadaPorConsumo = useAppSelector(t11);
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = selectIsTrialBlocked("NEGOCIACAO");
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const negociacaoBlocked = useAppSelector(t12);
  const [cotaModalAberto, setCotaModalAberto] = React.useState(false);
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = selectActiveContractByCategory("NOTIFICACAO");
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const contratoNotificacao = useAppSelector(t13);
  let t14;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = selectActiveContractByCategory("NEGOCIACAO");
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  const contratoNegociacao = useAppSelector(t14);
  let t15;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = selectActiveContractByCategory("ARBITRAGEM");
    $[12] = t15;
  } else {
    t15 = $[12];
  }
  const contratoArbitragem = useAppSelector(t15);
  let t16;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = selectActiveContractLoading("NOTIFICACAO");
    $[13] = t16;
  } else {
    t16 = $[13];
  }
  const carregandoNotificacao = useAppSelector(t16);
  let t17;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = selectActiveContractLoading("NEGOCIACAO");
    $[14] = t17;
  } else {
    t17 = $[14];
  }
  const carregandoNegociacao = useAppSelector(t17);
  let t18;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = selectActiveContractLoading("ARBITRAGEM");
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  const carregandoArbitragem = useAppSelector(t18);
  let t19;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = selectActiveContractError("NOTIFICACAO");
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  const erroNotificacao = useAppSelector(t19);
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = selectActiveContractError("NEGOCIACAO");
    $[17] = t20;
  } else {
    t20 = $[17];
  }
  const erroNegociacao = useAppSelector(t20);
  let t21;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = selectActiveContractError("ARBITRAGEM");
    $[18] = t21;
  } else {
    t21 = $[18];
  }
  const erroArbitragem = useAppSelector(t21);
  let t22;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = selectActiveContractResolvido("NOTIFICACAO");
    $[19] = t22;
  } else {
    t22 = $[19];
  }
  const notificacaoResolvida = useAppSelector(t22);
  let t23;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = selectActiveContractResolvido("NEGOCIACAO");
    $[20] = t23;
  } else {
    t23 = $[20];
  }
  const negociacaoResolvida = useAppSelector(t23);
  let t24;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = selectEstadoCriacao("ARBITRAGEM");
    $[21] = t24;
  } else {
    t24 = $[21];
  }
  const estadoCriacaoArbitral = useAppSelector(t24);
  const podeCriarLoteArbitral = useAppSelector(selectPodeCriarLoteArbitral);
  let t25;
  if ($[22] !== selected?.id) {
    t25 = state => selectPodeUsarSimulacao(state, selected?.id ?? null);
    $[22] = selected?.id;
    $[23] = t25;
  } else {
    t25 = $[23];
  }
  const podeUsarSimulacao = useAppSelector(t25);
  const gatesDeCriacao = {
    NOTIFICACAO: {
      resolvido: notificacaoResolvida,
      erro: erroNotificacao !== null,
      hasContract: hasNotificacaoContract,
      atLimit: atNotificacaoTrialLimit || notificacaoBloqueadaPorConsumo,
      blocked: notificacaoBlocked,
      listPath: "/notificacoes"
    },
    NEGOCIACAO: {
      resolvido: negociacaoResolvida,
      erro: erroNegociacao !== null,
      hasContract: hasNegociacaoContract,
      atLimit: atNegociacaoTrialLimit || negociacaoBloqueadaPorConsumo,
      blocked: negociacaoBlocked,
      listPath: "/negociacoes"
    }
  };
  const sidebarSaldos = isGuestWorkspace ? {
    visible: [],
    overflow: 0
  } : pickSidebarSaldos([buildSaldoCicloView(contratoNotificacao, {
    loading: carregandoNotificacao && !contratoNotificacao,
    error: erroNotificacao
  }), buildSaldoCicloView(contratoNegociacao, {
    loading: carregandoNegociacao && !contratoNegociacao,
    error: erroNegociacao
  }), buildSaldoCicloView(contratoArbitragem, {
    loading: carregandoArbitragem && !contratoArbitragem,
    error: erroArbitragem
  })], 1);
  let t26;
  if ($[24] !== dispatch || $[25] !== isGuestWorkspace || $[26] !== selected) {
    t26 = () => {
      if (isGuestWorkspace || !selected?.id) {
        return;
      }
      CATEGORIAS_COM_CONTRATO.forEach(categoria => {
        dispatch(fetchActiveContractByCategory(categoria));
      });
      dispatch(buscarAcessoSimulacao(selected.id));
    };
    $[24] = dispatch;
    $[25] = isGuestWorkspace;
    $[26] = selected;
    $[27] = t26;
  } else {
    t26 = $[27];
  }
  const t27 = selected?.id;
  let t28;
  if ($[28] !== dispatch || $[29] !== isGuestWorkspace || $[30] !== t27) {
    t28 = [dispatch, isGuestWorkspace, t27];
    $[28] = dispatch;
    $[29] = isGuestWorkspace;
    $[30] = t27;
    $[31] = t28;
  } else {
    t28 = $[31];
  }
  React.useEffect(t26, t28);
  let t29;
  if ($[32] !== onNavigate || $[33] !== router) {
    t29 = path => {
      router.push(path);
      if (onNavigate) {
        onNavigate();
      }
    };
    $[32] = onNavigate;
    $[33] = router;
    $[34] = t29;
  } else {
    t29 = $[34];
  }
  const handleNavigate = t29;
  const handleGatedNavigate = (category, path_0) => {
    if (category === "ARBITRAGEM") {
      const destinoArbitral = destinoDaCriacaoArbitral(estadoCriacaoArbitral, path_0);
      if (destinoArbitral !== path_0) {
        registrarAcao("arbitral.criacao.bloqueada", {
          estadoCriacao: estadoCriacaoArbitral,
          origem: "sidebar"
        });
      }
      handleNavigate(destinoArbitral);
      return;
    }
    const destino = destinoDaCriacao(category, gatesDeCriacao[category], path_0);
    if (destino.tipo === "modalCota") {
      setCotaModalAberto(true);
      return;
    }
    handleNavigate(destino.path);
  };
  let t30;
  if ($[35] !== isCollapsed || $[36] !== isMobile || $[37] !== onToggleCollapse) {
    t30 = !isMobile && onToggleCollapse ? <SidebarWithCollapsibleSections.NavItem className={getNavItemClassName(isCollapsed)} icon={isCollapsed ? <FeatherChevronsRight /> : <FeatherChevronsLeft />} isCollapsed={isCollapsed} {...getInteractiveProps(onToggleCollapse)}>{isCollapsed ? "Expandir" : "Recolher menu"}</SidebarWithCollapsibleSections.NavItem> : undefined;
    $[35] = isCollapsed;
    $[36] = isMobile;
    $[37] = onToggleCollapse;
    $[38] = t30;
  } else {
    t30 = $[38];
  }
  const footerToggle = t30;
  let t31;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setCotaModalAberto(false);
    $[39] = t31;
  } else {
    t31 = $[39];
  }
  let t32;
  if ($[40] !== handleNavigate) {
    t32 = () => {
      setCotaModalAberto(false);
      handleNavigate(ROTA_ABA_PLANO);
    };
    $[40] = handleNavigate;
    $[41] = t32;
  } else {
    t32 = $[41];
  }
  const t33 = notificacaoStatus === "TRIAL";
  const t34 = notificacaoPeriod?.totalBalance ?? null;
  let t35;
  if ($[42] !== cotaModalAberto || $[43] !== t32 || $[44] !== t33 || $[45] !== t34) {
    t35 = <CotaEsgotadaModal open={cotaModalAberto} onClose={t31} onVerPlanos={t32} emTrial={t33} totalBalance={t34} />;
    $[42] = cotaModalAberto;
    $[43] = t32;
    $[44] = t33;
    $[45] = t34;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  const T0 = SidebarWithCollapsibleSections;
  const t36 = "app-sidebar";
  const t37 = `${styles.root} ${isMobile ? "flex" : "hidden lg:flex"}`;
  let t38;
  if ($[47] !== favorites || $[48] !== isCollapsed || $[49] !== isMobile || $[50] !== items || $[51] !== loadMore || $[52] !== loading || $[53] !== openCreateModal || $[54] !== query || $[55] !== selectEscritorio || $[56] !== selected || $[57] !== setQuery || $[58] !== toggleFavorite || $[59] !== total) {
    t38 = !isMobile ? <div className="flex w-full flex-col py-2" style={{
      gap: isCollapsed ? "8px" : "16px",
      transition: "gap 350ms cubic-bezier(0.4, 0, 0.2, 1)"
    }}><div className="relative flex w-full items-center" style={{
        height: "40px"
      }}><div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isCollapsed ? 1 : 0,
          transform: isCollapsed ? "scale(1)" : "scale(0.7)",
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isCollapsed ? "auto" : "none"
        }}><Image src="/icone.svg" alt="Arbitralis" width={32} height={32} className="h-8 w-8" /></div><div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          opacity: isCollapsed ? 0 : 1,
          transform: isCollapsed ? "translateX(-8px)" : "translateX(0px)",
          transition: "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 350ms cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: isCollapsed ? "none" : "auto"
        }}><Image src="/Logo.svg" alt="Arbitralis" width={160} height={32} className="h-8 w-auto" /></div></div><div className="w-full transition-all duration-300"><WorkspaceSwitcher selected={selected} items={items} loading={loading} total={total} query={query} favorites={favorites} onQueryChange={setQuery} onToggleFavorite={toggleFavorite} onSelectWorkspace={workspace => void selectEscritorio(workspace.id)} onLoadMore={loadMore} onCreateClick={openCreateModal} isCollapsed={isCollapsed} /></div></div> : null;
    $[47] = favorites;
    $[48] = isCollapsed;
    $[49] = isMobile;
    $[50] = items;
    $[51] = loadMore;
    $[52] = loading;
    $[53] = openCreateModal;
    $[54] = query;
    $[55] = selectEscritorio;
    $[56] = selected;
    $[57] = setQuery;
    $[58] = toggleFavorite;
    $[59] = total;
    $[60] = t38;
  } else {
    t38 = $[60];
  }
  const t39 = !isDesktopCollapsed && sidebarSaldos.visible.length > 0 && <div className="flex w-full flex-col gap-2"><p className="px-1 text-[11px] font-medium tracking-wide text-neutral-600 uppercase">Seus planos</p>{sidebarSaldos.visible.map(saldo => <SaldoCicloCard key={saldo.productName} {...saldo} showLabel={false} onVerPlano={() => handleNavigate(ROTA_ABA_PLANO)} onRenovar={() => handleNavigate(ROTA_ABA_PLANO)} />)}{sidebarSaldos.overflow > 0 && <button type="button" className="text-brand-600 px-1 text-left text-xs font-medium hover:underline" onClick={() => handleNavigate(ROTA_ABA_PLANO)}>+{sidebarSaldos.overflow}{" "}{sidebarSaldos.overflow === 1 ? "outro plano" : "outros planos"}</button>}</div>;
  let t40;
  if ($[61] !== isDesktopCollapsed) {
    t40 = <IrParaPainelDoRequerido isCollapsed={isDesktopCollapsed} />;
    $[61] = isDesktopCollapsed;
    $[62] = t40;
  } else {
    t40 = $[62];
  }
  let t41;
  if ($[63] !== footerToggle || $[64] !== t39 || $[65] !== t40) {
    t41 = <>{t39}{t40}{footerToggle}</>;
    $[63] = footerToggle;
    $[64] = t39;
    $[65] = t40;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  const t42 = "flex w-full flex-col items-start gap-1";
  const t43 = isGuestWorkspace ? <SidebarWithCollapsibleSections.NavItem className={getNavItemClassName(isCollapsed)} selected={pathname.startsWith("/notificacoes")} icon={<FeatherSend />} isCollapsed={isDesktopCollapsed} {...getInteractiveProps(() => handleNavigate("/notificacoes"))}>Notificações compartilhadas</SidebarWithCollapsibleSections.NavItem> : <><SidebarWithCollapsibleSections.NavItem className={getNavItemClassName(isCollapsed)} selected={pathname === "/"} icon={<FeatherHome />} isCollapsed={isDesktopCollapsed} {...getInteractiveProps(() => handleNavigate("/"))}>Home</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.CollapsibleNavItem label={"Notifica\xE7\xF5es"} icon={<FeatherSend />} selected={pathname.includes("/notificacoes")} isCollapsed={isDesktopCollapsed}><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherPlus />} selected={pathname === "/notificacoes/criar/individual"} {...getInteractiveProps(() => handleGatedNavigate("NOTIFICACAO", "/notificacoes/criar/individual"))}>Criar notificação</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherLayers />} selected={pathname === "/notificacoes/criar/em-massa"} {...getInteractiveProps(() => handleGatedNavigate("NOTIFICACAO", "/notificacoes/criar/em-massa"))}>Criar lote de notificações</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherEye />} selected={pathname === "/notificacoes"} {...getInteractiveProps(() => handleNavigate("/notificacoes"))}>Ver notificações</SidebarWithCollapsibleSections.NavItem></SidebarWithCollapsibleSections.CollapsibleNavItem></>;
  const t44 = !isGuestWorkspace && <><SidebarWithCollapsibleSections.CollapsibleNavItem label={"Negocia\xE7\xF5es"} icon={<FeatherPiggyBank />} selected={pathname.includes("/negociacoes")} isCollapsed={isDesktopCollapsed}><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherPlus />} selected={pathname.startsWith("/negociacoes/nova/individual")} {...getInteractiveProps(() => handleGatedNavigate("NEGOCIACAO", "/negociacoes/nova/individual/agentes"))}>Criar negociação</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherLayers />} selected={pathname.startsWith("/negociacoes/nova/massa")} {...getInteractiveProps(() => handleGatedNavigate("NEGOCIACAO", "/negociacoes/nova/massa"))}>Criar lote de negociações</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherEye />} selected={pathname === "/negociacoes"} {...getInteractiveProps(() => handleNavigate("/negociacoes"))}>Ver negociações</SidebarWithCollapsibleSections.NavItem>{podeUsarSimulacao && <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherMessageCircle />} selected={pathname.startsWith("/negociacoes/simulacao")} {...getInteractiveProps(() => handleGatedNavigate("NEGOCIACAO", "/negociacoes/simulacao"))}>Testar meus agentes</SidebarWithCollapsibleSections.NavItem>}</SidebarWithCollapsibleSections.CollapsibleNavItem><SidebarWithCollapsibleSections.NavItem className={getNavItemClassName(isCollapsed)} selected={pathname.startsWith("/regua-de-cobranca") || pathname.startsWith("/templates/regua")} icon={<FeatherListChecks />} isCollapsed={isDesktopCollapsed} rightSlot={!isDesktopCollapsed ? <NovoBadge /> : null} {...getInteractiveProps(() => handleNavigate("/regua-de-cobranca"))}>Régua de cobrança</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.CollapsibleNavItem label="Processo arbitral" icon={<FeatherScale />} selected={pathname.includes("/arbitral")} isCollapsed={isDesktopCollapsed}><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherPlus />} selected={pathname.startsWith("/arbitral/novo")} {...getInteractiveProps(() => handleGatedNavigate("ARBITRAGEM", "/arbitral/novo"))}>Criar processo arbitral</SidebarWithCollapsibleSections.NavItem>{podeCriarLoteArbitral && <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherLayers />} selected={pathname.startsWith("/arbitral/lote")} {...getInteractiveProps(() => handleGatedNavigate("ARBITRAGEM", "/arbitral/lote"))}>Criar em lote</SidebarWithCollapsibleSections.NavItem>}<SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherEye />} selected={pathname === "/arbitral"} {...getInteractiveProps(() => handleNavigate("/arbitral"))}>Ver processos</SidebarWithCollapsibleSections.NavItem></SidebarWithCollapsibleSections.CollapsibleNavItem><SidebarWithCollapsibleSections.CollapsibleNavItem label="Chat" icon={<FeatherMessageCircle />} rightSlot={!isDesktopCollapsed ? <NovoBadge /> : null} selected={pathname.startsWith("/chat")} isCollapsed={isDesktopCollapsed}><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherMessageCircle />} selected={pathname === "/chat"} {...getInteractiveProps(() => handleNavigate("/chat"))}>Central de atendimento</SidebarWithCollapsibleSections.NavItem></SidebarWithCollapsibleSections.CollapsibleNavItem></>;
  let t45;
  if ($[67] !== t43 || $[68] !== t44) {
    t45 = <div className={t42}>{t43}{t44}</div>;
    $[67] = t43;
    $[68] = t44;
    $[69] = t45;
  } else {
    t45 = $[69];
  }
  let t46;
  if ($[70] !== handleNavigate || $[71] !== isDesktopCollapsed || $[72] !== isGuestWorkspace || $[73] !== pathname || $[74] !== user) {
    t46 = !isGuestWorkspace && <div className="flex w-full flex-col items-start gap-1 border-t border-neutral-200 pt-3"><SidebarWithCollapsibleSections.CollapsibleNavItem label={"Configura\xE7\xF5es"} icon={<FeatherSettings />} selected={pathname.startsWith("/configuracoes/financeiro") || pathname === "/configuracoes/chaves-de-api" || pathname === "/configuracoes/integracoes" || pathname === "/painel-advogado" || pathname === "/templates" || pathname === "/configuracoes/documentos-padrao"} isCollapsed={isDesktopCollapsed}>{ehAdminDoEscritorio(user?.papelEscritorio) ? <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherDollarSign />} selected={pathname.startsWith("/configuracoes/financeiro")} {...getInteractiveProps(() => handleNavigate("/configuracoes/financeiro"))}>Financeiro</SidebarWithCollapsibleSections.NavItem> : <DisabledNavItem label="Financeiro" icon={<FeatherDollarSign />} />}{podeAdministrarChaves(user) ? <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherKey />} selected={pathname === "/configuracoes/chaves-de-api"} {...getInteractiveProps(() => handleNavigate("/configuracoes/chaves-de-api"))}>Chaves de API</SidebarWithCollapsibleSections.NavItem> : <DisabledNavItem label="Chaves de API" icon={<FeatherKey />} />}{podeAdministrarChaves(user) ? <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherShare2 />} selected={pathname === "/configuracoes/integracoes"} {...getInteractiveProps(() => handleNavigate("/configuracoes/integracoes"))}>Integrações</SidebarWithCollapsibleSections.NavItem> : <DisabledNavItem label={"Integra\xE7\xF5es"} icon={<FeatherShare2 />} />}{user?.advogadoPainelAtivo && <SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherLayoutDashboard />} selected={pathname === "/painel-advogado"} {...getInteractiveProps(() => handleNavigate("/painel-advogado"))}>Painel do Advogado</SidebarWithCollapsibleSections.NavItem>}<SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherPuzzle />} selected={pathname === "/templates"} {...getInteractiveProps(() => handleNavigate("/templates"))}>Templates</SidebarWithCollapsibleSections.NavItem><SidebarWithCollapsibleSections.NavItem className="text-body-xs h-8" icon={<FeatherBookOpen />} selected={pathname === "/configuracoes/documentos-padrao"} {...getInteractiveProps(() => handleNavigate("/configuracoes/documentos-padrao"))}>Documentos padrão</SidebarWithCollapsibleSections.NavItem></SidebarWithCollapsibleSections.CollapsibleNavItem></div>;
    $[70] = handleNavigate;
    $[71] = isDesktopCollapsed;
    $[72] = isGuestWorkspace;
    $[73] = pathname;
    $[74] = user;
    $[75] = t46;
  } else {
    t46 = $[75];
  }
  let t47;
  if ($[76] !== isDesktopCollapsed || $[77] !== isGuestWorkspace) {
    t47 = !isGuestWorkspace && !isDesktopCollapsed && <details className={`${styles.comingSoon} w-full rounded-[16px] border border-neutral-200 bg-white`}><summary className="focus-visible:outline-brand-500 flex min-h-10 cursor-pointer items-center gap-2 rounded-[16px] px-3 py-2 text-xs font-medium text-neutral-600 focus-visible:outline-2 focus-visible:-outline-offset-2"><span className="flex-1">Em breve na Arbitralis</span><span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px]">2</span><FeatherChevronDown className="size-3.5 transition-transform" aria-hidden="true" /></summary><div className="grid grid-cols-2 gap-2 px-3 pb-3"><div className="flex flex-col gap-2 rounded-[16px] bg-neutral-50 p-3"><FeatherHeartHandshake className="size-4 text-neutral-500" aria-hidden="true" /><span className="text-xs leading-relaxed text-neutral-600">Mediação e Conciliação</span></div><div className="flex flex-col gap-2 rounded-[16px] bg-neutral-50 p-3"><FeatherGavel className="size-4 text-neutral-500" aria-hidden="true" /><span className="text-xs leading-relaxed text-neutral-600">Peticionamento</span></div></div></details>;
    $[76] = isDesktopCollapsed;
    $[77] = isGuestWorkspace;
    $[78] = t47;
  } else {
    t47 = $[78];
  }
  let t48;
  if ($[79] !== isDesktopCollapsed || $[80] !== isGuestWorkspace || $[81] !== onNavigate || $[82] !== selected?.id) {
    t48 = !isGuestWorkspace && selected?.id ? <ChecklistConfiguracao escritorioId={selected?.id ?? null} isCollapsed={isDesktopCollapsed} onNavigate={onNavigate} /> : null;
    $[79] = isDesktopCollapsed;
    $[80] = isGuestWorkspace;
    $[81] = onNavigate;
    $[82] = selected?.id;
    $[83] = t48;
  } else {
    t48 = $[83];
  }
  let t49;
  if ($[84] !== T0 || $[85] !== isDesktopCollapsed || $[86] !== t37 || $[87] !== t38 || $[88] !== t41 || $[89] !== t45 || $[90] !== t46 || $[91] !== t47 || $[92] !== t48) {
    t49 = <T0 data-testid={t36} className={t37} data-collapsed={isDesktopCollapsed} isCollapsed={isDesktopCollapsed} header={t38} footer={t41}>{t45}{t46}{t47}{t48}</T0>;
    $[84] = T0;
    $[85] = isDesktopCollapsed;
    $[86] = t37;
    $[87] = t38;
    $[88] = t41;
    $[89] = t45;
    $[90] = t46;
    $[91] = t47;
    $[92] = t48;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  let t50;
  if ($[94] !== closeCreateModal || $[95] !== createOpen || $[96] !== handleCreateSuccess) {
    t50 = <CreateEscritorioModal isOpen={createOpen} onClose={closeCreateModal} onSuccess={handleCreateSuccess} />;
    $[94] = closeCreateModal;
    $[95] = createOpen;
    $[96] = handleCreateSuccess;
    $[97] = t50;
  } else {
    t50 = $[97];
  }
  let t51;
  if ($[98] !== t35 || $[99] !== t49 || $[100] !== t50) {
    t51 = <>{t35}{t49}{t50}</>;
    $[98] = t35;
    $[99] = t49;
    $[100] = t50;
    $[101] = t51;
  } else {
    t51 = $[101];
  }
  return t51;
};